import { useMemo } from 'react'

import {
  IConditionalField,
  IPermitTypeField,
  PermitFieldType,
} from '~/client/graph/types.generated'
import { serializeConditionalKey } from '~/client/src/shared/utils/conditionalKeyUtils'

import IConditionalHandlersProps from '../types/IConditionalHandlersProps'
import IDependentFieldsHandlersProps from '../types/IDependentFieldsHandlersProps'

interface UseHandlersProps extends IConditionalHandlersProps {
  field: IPermitTypeField
  allRules: IConditionalField[]
  isLBSToggleEnabled: boolean

  onToggleRule: (key: string) => void
}

export function useHandlers({
  field,
  allRules,
  isLBSToggleEnabled,
  addNewConditionalField,
  updateConditionalField,
  removeConditionalField,
  onToggleRule,
}: UseHandlersProps): IDependentFieldsHandlersProps {
  const findRule = useMemo(
    () => (ruleKey: string) => allRules.find(r => r.key === ruleKey),
    [allRules],
  )

  const createNewRule = useMemo(
    () => (value: string, fieldType?: PermitFieldType) => {
      if (!value) return
      addNewConditionalField(field.id, value, -1, fieldType)
      onToggleRule(value)
    },
    [field.id, addNewConditionalField, onToggleRule],
  )

  const handlers: IDependentFieldsHandlersProps = useMemo(
    () => ({
      handleCreateNewRule: createNewRule,

      handleNewRuleValueSelect: (
        event: React.ChangeEvent<HTMLSelectElement>,
      ) => {
        createNewRule(event.target.value)
      },

      handleLocationApply: (
        selectedIds: string[],
        fieldType?: PermitFieldType,
      ) => {
        if (!selectedIds.length) return
        const key = serializeConditionalKey(selectedIds)
        createNewRule(key, fieldType)
      },

      handleEditRuleLocations: (oldKey: string, newSelectedIds: string[]) => {
        if (!newSelectedIds.length) return
        const newKey = serializeConditionalKey(newSelectedIds)
        if (newKey === oldKey) return

        const rule = findRule(oldKey)
        if (!rule) return

        // Mutate key on the MobX observable and trigger save
        rule.key = newKey
        if (rule.values.length > 0) {
          updateConditionalField(rule.values, 0, rule.values[0])
        }
      },

      handleAddField: (
        ruleKey: string,
        index: number,
        fieldType: PermitFieldType,
      ) => {
        const rule = findRule(ruleKey)
        if (!rule) return
        addNewConditionalField(
          field.id,
          ruleKey,
          index,
          fieldType,
          null,
          isLBSToggleEnabled,
        )
      },

      handleUpdateField: (
        ruleKey: string,
        index: number,
        updatedField: IPermitTypeField,
      ) => {
        const rule = findRule(ruleKey)
        if (!rule) return
        updateConditionalField(rule.values, index, updatedField)
      },

      handleRemoveField: (
        ruleKey: string,
        conditionalField: IPermitTypeField,
      ) => {
        removeConditionalField(field.id, ruleKey, conditionalField.id)
      },

      handleRemoveRule: (ruleKey: string) => {
        removeConditionalField(field.id, ruleKey, null)
      },
    }),
    [
      field.id,
      findRule,
      createNewRule,
      addNewConditionalField,
      updateConditionalField,
      removeConditionalField,
    ],
  )

  return handlers
}
